import * as React from 'react'
import {
  AlertTriangle, KeyRound, LogOut, Pencil, Search, ShieldCheck, Trash2,
  User as UserIcon, UserPlus,
} from 'lucide-react'

import {
  useAdminUsers, useCreateAdminUser, useDeleteAdminUser, useResetUserPassword,
  useRevokeUserSessions, useUpdateAdminUser,
  type AdminUser, type PlanOption,
} from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { cn, formatDateTime } from '@/lib/utils'

const STATUS: Record<AdminUser['status'], { label: string; variant: 'default' | 'warning' | 'danger' | 'muted' }> = {
  active: { label: '正常', variant: 'default' },
  pending: { label: '待验证', variant: 'warning' },
  suspended: { label: '已停用', variant: 'danger' },
  deleted: { label: '已删除', variant: 'muted' },
}

/**
 * 用户管理。
 *
 * 收款还没开（§18.1 的排期决策），所以套餐是人工指派的——这个页面就是那个"人工"。
 */
export default function UsersPage() {
  const { user: me } = useAuth()
  const [search, setSearch] = React.useState('')
  const [query, setQuery] = React.useState('')
  const [role, setRole] = React.useState('')
  const [status, setStatus] = React.useState('')

  const [creating, setCreating] = React.useState(false)

  const { data, isLoading, error, refetch } = useAdminUsers({
    search: query || undefined,
    role: role || undefined,
    status: status || undefined,
    limit: 100,
  })

  return (
    <div>
      <PageHeader
        title="用户管理"
        description="角色、状态与套餐。停用、删除和重置密码都会同时吊销该账号的全部会话。"
        actions={
          <Button onClick={() => setCreating(true)}>
            <UserPlus className="h-3.5 w-3.5" aria-hidden /> 新建用户
          </Button>
        }
      />

      {creating && (
        <CreateUserDialog
          plans={data?.plans ?? []}
          open
          onOpenChange={(o) => { if (!o) setCreating(false) }}
        />
      )}

      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">搜索</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                className="h-9 w-64 pl-8" value={search} placeholder="邮箱或昵称，回车搜索"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setQuery(search.trim()) }}
                onBlur={() => setQuery(search.trim())}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">角色</Label>
            <Select value={role || 'all'} onValueChange={(v) => setRole(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="admin">管理员</SelectItem>
                <SelectItem value="user">普通用户</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">状态</Label>
            <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="active">正常</SelectItem>
                <SelectItem value="pending">待验证</SelectItem>
                <SelectItem value="suspended">已停用</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {data && (
            <span className="ml-auto self-center text-xs text-muted-foreground">
              共 {data.total} 个账号
            </span>
          )}
        </div>

        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data || data.users.length === 0 ? (
          <EmptyState title="没有匹配的账号" description="换个搜索条件试试。" icon={UserIcon} />
        ) : (
          <UserTable users={data.users} plans={data.plans} meId={me?.id ?? ''} />
        )}
      </div>
    </div>
  )
}

function UserTable({
  users, plans, meId,
}: { users: AdminUser[]; plans: PlanOption[]; meId: string }) {
  const toast = useToast()
  const update = useUpdateAdminUser()
  const revoke = useRevokeUserSessions()
  const [resetFor, setResetFor] = React.useState<AdminUser | null>(null)
  const [editFor, setEditFor] = React.useState<AdminUser | null>(null)
  const [deleteFor, setDeleteFor] = React.useState<AdminUser | null>(null)

  async function patch(u: AdminUser, p: Parameters<typeof update.mutateAsync>[0]['patch'], what: string) {
    try {
      await update.mutateAsync({ id: u.id, patch: p })
      toast.success(`已更新${what}`, u.email)
    } catch (err) {
      toast.error('更新失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <>
      <div className="scroll-x rounded-lg border">
        <table className="w-full min-w-[1000px] text-sm">
          <caption className="sr-only">用户列表，共 {users.length} 行</caption>
          <thead className="border-b bg-muted/40">
            <tr>
              {['账号', '角色', '状态', '套餐', '监控/会话', '注册时间', '最后登录', '操作'].map((h) => (
                <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.id === meId
              return (
                <tr key={u.id} className={cn('border-b last:border-0 hover:bg-muted/40',
                  u.status === 'suspended' && 'bg-destructive/5')}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{u.display_name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                    {isMe && <Badge variant="secondary" className="mt-1">这是你</Badge>}
                  </td>

                  <td className="px-3 py-2">
                    <Select
                      value={u.role}
                      disabled={isMe}
                      onValueChange={(v) => void patch(u, { role: v }, '角色')}
                    >
                      <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">普通用户</SelectItem>
                        <SelectItem value="admin">管理员</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>

                  <td className="px-3 py-2">
                    {isMe ? (
                      <Tooltip content="不能停用自己的账号——这是唯一一个在产品内无法自行恢复的操作">
                        <Badge variant={STATUS[u.status].variant} className="cursor-help">
                          {STATUS[u.status].label}
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Select value={u.status} onValueChange={(v) => void patch(u, { status: v }, '状态')}>
                        <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">正常</SelectItem>
                          <SelectItem value="pending">待验证</SelectItem>
                          <SelectItem value="suspended">已停用</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>

                  <td className="px-3 py-2">
                    <Select
                      value={u.plan_version_id || 'none'}
                      onValueChange={(v) => void patch(u, { plan_version_id: v === 'none' ? '' : v }, '套餐')}
                    >
                      <SelectTrigger className="h-8 w-36"><SelectValue placeholder="未指派" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">未指派</SelectItem>
                        {plans.map((p) => (
                          <SelectItem key={p.version_id} value={p.version_id}>
                            {p.name} v{p.version}{p.is_current ? '' : '（旧版）'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>

                  <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                    {u.active_watchers} / {u.active_sessions}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {formatDateTime(u.created_at)}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : '从未登录'}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Tooltip content="编辑昵称与邮箱">
                        <Button variant="ghost" size="icon-sm"
                          onClick={() => setEditFor(u)}
                          aria-label={`编辑 ${u.email}`}>
                          <Pencil className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </Tooltip>
                      <Tooltip content="重置密码：生成一个临时密码并吊销该账号全部会话">
                        <Button variant="ghost" size="icon-sm"
                          onClick={() => setResetFor(u)}
                          aria-label={`重置 ${u.email} 的密码`}>
                          <KeyRound className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </Tooltip>
                      <Tooltip content={u.active_sessions > 0
                        ? `强制下线（当前 ${u.active_sessions} 个会话）`
                        : '当前没有活跃会话'}>
                        <Button variant="ghost" size="icon-sm"
                          disabled={u.active_sessions === 0}
                          onClick={async () => {
                            try {
                              const res = await revoke.mutateAsync(u.id)
                              toast.success('已强制下线', `吊销 ${res.sessions_revoked} 个会话`)
                            } catch (err) {
                              toast.error('操作失败', err instanceof ApiError ? err.message : undefined)
                            }
                          }}
                          aria-label={`强制 ${u.email} 下线`}>
                          <LogOut className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </Tooltip>
                      <Tooltip content={isMe
                        ? '不能删除自己的账号——这是唯一一个在产品内无法自行恢复的操作'
                        : '删除账号：立即失效并释放该邮箱，历史记录保留'}>
                        <Button variant="ghost" size="icon-sm"
                          disabled={isMe}
                          onClick={() => setDeleteFor(u)}
                          aria-label={`删除 ${u.email}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                        </Button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {resetFor && (
        <ResetPasswordDialog user={resetFor} open onOpenChange={(o) => { if (!o) setResetFor(null) }} />
      )}
      {editFor && (
        <EditUserDialog user={editFor} open onOpenChange={(o) => { if (!o) setEditFor(null) }} />
      )}
      {deleteFor && (
        <DeleteUserDialog user={deleteFor} open onOpenChange={(o) => { if (!o) setDeleteFor(null) }} />
      )}
    </>
  )
}

/**
 * 重置密码。
 *
 * 生成的密码**只显示这一次**：它不写审计、不落日志、服务端也不留可读副本。
 * 这正是敢于代生成的前提——它只在这个对话框里存在过。
 */
function ResetPasswordDialog({
  user, open, onOpenChange,
}: { user: AdminUser; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast()
  const reset = useResetUserPassword()
  const [custom, setCustom] = React.useState('')
  const [issued, setIssued] = React.useState<string | null>(null)

  async function run() {
    try {
      const res = await reset.mutateAsync({ id: user.id, password: custom || undefined })
      setIssued(res.password ?? '（已设置为你输入的密码）')
    } catch (err) {
      toast.error('重置失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>重置密码</DialogTitle>
          <DialogDescription>
            为 {user.email} 设置新密码，并<strong>吊销其全部会话</strong>。
            不吊销的话旧登录态还在，重置就等于没做。
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="space-y-2">
            <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
              <span>这个密码<strong>只显示这一次</strong>，关闭后无法再查看。请立即转交用户，并要求其登录后自行修改。</span>
            </p>
            <code className="block select-all break-all rounded bg-muted p-3 font-mono text-sm">
              {issued}
            </code>
          </div>
        ) : (
          <div className="space-y-1">
            <Label className="text-xs">自定义密码（留空则自动生成）</Label>
            <Input
              type="text" value={custom} placeholder="留空 = 生成一个强随机密码"
              onChange={(e) => setCustom(e.target.value)}
            />
          </div>
        )}

        <DialogFooter>
          {issued ? (
            <Button onClick={() => onOpenChange(false)}>
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> 我已保存
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
              <Button variant="destructive" onClick={() => void run()} disabled={reset.isPending}>
                重置并强制下线
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 编辑账号资料。
 *
 * 昵称只是个标签，邮箱不是——它是登录身份。改掉邮箱等于把这个账号交给另一个
 * 地址，所以服务端会顺手清掉验证时间戳（新地址还没被任何人证明过），但不动
 * 状态：管理员亲手改的地址，本身就是一次背书。
 */
function EditUserDialog({
  user, open, onOpenChange,
}: { user: AdminUser; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast()
  const update = useUpdateAdminUser()
  const [displayName, setDisplayName] = React.useState(user.display_name)
  const [email, setEmail] = React.useState(user.email)

  const emailChanged = email.trim().toLowerCase() !== user.email.toLowerCase()
  const dirty = emailChanged || displayName.trim() !== user.display_name

  async function save() {
    try {
      await update.mutateAsync({
        id: user.id,
        patch: {
          display_name: displayName.trim(),
          ...(emailChanged ? { email: email.trim().toLowerCase() } : {}),
        },
      })
      toast.success('已更新账号资料', email.trim().toLowerCase())
      onOpenChange(false)
    } catch (err) {
      toast.error('更新失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>编辑账号资料</DialogTitle>
          <DialogDescription>角色、状态和套餐在表格里直接改，这里只管昵称和邮箱。</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="edit-user-name" className="text-xs">昵称</Label>
            <Input
              id="edit-user-name" value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="留空则显示邮箱前缀"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-user-email" className="text-xs">邮箱（登录账号）</Label>
            <Input
              id="edit-user-email" type="email" value={email} autoComplete="off"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {emailChanged && (
            <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
              <span>
                改邮箱等于换登录账号：该用户此后要用 <strong>{email.trim().toLowerCase()}</strong> 登录，
                邮箱验证状态会被清空。记得当面通知本人。
              </span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          <Button onClick={() => void save()} disabled={!dirty || update.isPending}>
            {update.isPending ? '保存中…' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 新建用户。
 *
 * 不走自助注册那条路：那条路默认是 pending（邮箱没人证明过），而这里是管理员
 * 亲手开的号，默认就能登录。让操作者建完还得再去翻一个验证开关，只会教他忽略
 * 那个开关。
 */
function CreateUserDialog({
  plans, open, onOpenChange,
}: { plans: PlanOption[]; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast()
  const create = useCreateAdminUser()
  const [email, setEmail] = React.useState('')
  const [displayName, setDisplayName] = React.useState('')
  const [role, setRole] = React.useState('user')
  const [planVersionID, setPlanVersionID] = React.useState('none')
  const [password, setPassword] = React.useState('')
  const [issued, setIssued] = React.useState<{ email: string; password: string } | null>(null)

  async function submit() {
    try {
      const res = await create.mutateAsync({
        email: email.trim().toLowerCase(),
        display_name: displayName.trim() || undefined,
        role,
        password: password || undefined,
        plan_version_id: planVersionID === 'none' ? undefined : planVersionID,
      })
      if (res.password) {
        setIssued({ email: res.user.email, password: res.password })
      } else {
        toast.success('已创建账号', res.user.email)
        onOpenChange(false)
      }
    } catch (err) {
      toast.error('创建失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>新建用户</DialogTitle>
          <DialogDescription>
            管理员亲手开的账号<strong>直接可登录</strong>，不需要邮箱验证。
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="space-y-2">
            <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
              <span>
                <strong>{issued.email}</strong> 的初始密码<strong>只显示这一次</strong>，
                关闭后无法再查看。请立即转交用户，并要求其登录后自行修改。
              </span>
            </p>
            <code className="block select-all break-all rounded bg-muted p-3 font-mono text-sm">
              {issued.password}
            </code>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="new-user-email" className="text-xs">邮箱（登录账号）</Label>
              <Input
                id="new-user-email" type="email" value={email} autoComplete="off"
                placeholder="someone@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-user-name" className="text-xs">昵称（可选）</Label>
              <Input
                id="new-user-name" value={displayName} placeholder="留空则取邮箱前缀"
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">角色</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">普通用户</SelectItem>
                    <SelectItem value="admin">管理员</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs">套餐</Label>
                <Select value={planVersionID} onValueChange={setPlanVersionID}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">默认（免费版）</SelectItem>
                    {plans.map((p) => (
                      <SelectItem key={p.version_id} value={p.version_id}>
                        {p.name} v{p.version}{p.is_current ? '' : '（旧版）'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-user-password" className="text-xs">初始密码（留空则自动生成）</Label>
              <Input
                id="new-user-password" type="text" value={password} autoComplete="off"
                placeholder="留空 = 生成一个强随机密码"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {issued ? (
            <Button onClick={() => onOpenChange(false)}>
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> 我已保存
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
              <Button onClick={() => void submit()} disabled={!email.trim() || create.isPending}>
                {create.isPending ? '创建中…' : '创建'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 删除账号。
 *
 * 软删除：这个账号名下有监控、规则、审计和账单历史，硬删要么把它们一起带走，
 * 要么直接被外键挡住。所以这里只打标记——登录、会话、API token 立刻全失效，
 * 历史照旧可查。
 *
 * 和"停用"的唯一区别是邮箱会被释放（0036 的部分唯一索引），本人可以拿同一个
 * 地址重新注册。只要不需要这一条，停用就够了。
 */
function DeleteUserDialog({
  user, open, onOpenChange,
}: { user: AdminUser; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast()
  const remove = useDeleteAdminUser()
  const [confirm, setConfirm] = React.useState('')

  async function run() {
    try {
      await remove.mutateAsync(user.id)
      toast.success('已删除账号', user.email)
      onOpenChange(false)
    } catch (err) {
      toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">确认删除账号？</DialogTitle>
          <DialogDescription>
            该账号会立即失效——全部会话和 API token 一并吊销，且 <strong>{user.email}</strong> 会
            被释放，可以重新注册。它名下的监控、规则和历史记录保留，不会一起删掉。
            只是想让它登不上来的话，把状态改成"已停用"就够了。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="delete-user-confirm" className="text-xs">
            输入 {user.email} 以确认
          </Label>
          <Input
            id="delete-user-confirm" value={confirm} autoComplete="off"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          <Button
            variant="destructive"
            onClick={() => void run()}
            disabled={remove.isPending || confirm.trim().toLowerCase() !== user.email.toLowerCase()}
          >
            {remove.isPending ? '删除中…' : '确认删除'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
